import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isUcanApp, getUcanAppPath } from '@/lib/ucanApp';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appleToken = params.get('apple_token');
  
    if (appleToken) {
      localStorage.setItem('jwt_token', appleToken);
      window.history.replaceState({}, document.title, '/login');
      navigate(getUcanAppPath('/dashboard'), { replace: true });
    }
  }, [navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Ingresa tu correo y contraseña');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      localStorage.setItem('jwt_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Sesión iniciada correctamente');
      
      const user = response.data.user;
      
      const params = new URLSearchParams(window.location.search);
      const redirectTo = params.get('redirect') || localStorage.getItem('ucan_after_login_redirect');

      // Check if user has multiple roles
      if (user.needs_role_selection && user.roles?.length > 1) {
        navigate(getUcanAppPath('/select-role'), { replace: true, state: { user, redirectTo } });
      } else if (redirectTo && redirectTo.startsWith('/provider/')) {
        localStorage.removeItem('ucan_after_login_redirect');
        navigate(getUcanAppPath(redirectTo), { replace: true });
      } else if (user.role === 'provider') {
        localStorage.removeItem('ucan_after_login_redirect');
        navigate(getUcanAppPath('/provider/dashboard'), { replace: true });
      } else {
        if (redirectTo && !redirectTo.startsWith('/provider/')) {
          localStorage.removeItem('ucan_after_login_redirect');
          navigate(getUcanAppPath(redirectTo), { replace: true });
        } else {
          localStorage.removeItem('ucan_after_login_redirect');
          navigate(isUcanApp() ? getUcanAppPath('/dashboard') : '/dashboard', { replace: true });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRedirect = () => {
    const appContext = isUcanApp() ||
      new URLSearchParams(window.location.search).get('ucan_app') === 'ios';

    if (appContext) {
      localStorage.setItem('ucan_app', 'ios');
    }

    const redirectTo = new URLSearchParams(window.location.search).get('redirect') || localStorage.getItem('ucan_after_login_redirect');
    if (redirectTo) {
      localStorage.setItem('ucan_after_login_redirect', redirectTo);
    }

    const redirectUri = `https://u-can.cl/auth/google/callback`;
    const scope = 'email profile openid';
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      access_type: 'offline',
      prompt: 'select_account',
      state: appContext ? 'ucan_app_ios' : 'ucan_web'
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };


  const handleAppleRedirect = () => {
    const appContext = isUcanApp() ||
      new URLSearchParams(window.location.search).get('ucan_app') === 'ios';

    if (appContext) {
      localStorage.setItem('ucan_app', 'ios');
    }

    const redirectUri = `https://u-can.cl/api/auth/apple/callback`;
    const scope = 'name email';
  
    const params = new URLSearchParams({
      client_id: APPLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope,
    });
  
    window.location.href = `https://appleid.apple.com/auth/authorize?${params.toString()}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <Link to="/" className="fixed top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-[#E6202E] transition-colors bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border" data-testid="back-home-link">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        <span className="text-sm font-medium">Volver al Inicio</span>
      </Link>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
                    <Link to="/"><img src="/ucan-logo-rojo.svg" alt="U-CAN" className="h-16 mx-auto mb-6" /></Link>
          <h2 className="font-montserrat text-4xl font-extrabold text-gray-900 uppercase" data-testid="login-title">
            Iniciar Sesión
          </h2>
          <p className="mt-2 text-gray-600">Accede a tu cuenta de U-CAN</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 py-6 rounded-xl border-gray-300"
                data-testid="login-email-input"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 py-6 rounded-xl border-gray-300"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#E6202E] hover:underline" data-testid="forgot-password-link">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E6202E] hover:bg-[#D31522] text-white py-6 text-lg rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
              data-testid="email-login-button"
            >
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-gray-500">o continúa con</span>
            </div>
          </div>

                    <div className="space-y-3" data-testid="social-login-container">
            <button
              type="button"
              onClick={handleAppleRedirect}
              className="flex items-center justify-center gap-3 w-full px-4 py-3 rounded-lg bg-black hover:bg-gray-900 transition-colors shadow-sm"
              data-testid="apple-login-button"
            >
             <svg
  className="w-5 h-5"
  viewBox="0 0 24 24"
  fill="white"
>
  <path d="M16.365 1.43c0 1.14-.42 2.23-1.16 3.02-.8.86-2.1 1.52-3.23 1.43-.14-1.09.4-2.25 1.13-3.01.8-.84 2.17-1.46 3.26-1.44zM19.37 17.16c-.54 1.2-.8 1.74-1.49 2.79-.96 1.46-2.31 3.28-3.98 3.29-1.49.01-1.88-.97-3.9-.96-2.02.01-2.45.98-3.94.97-1.67-.02-2.95-1.66-3.91-3.12-2.68-4.08-2.96-8.86-1.31-11.41 1.18-1.83 3.04-2.9 4.79-2.9 1.78 0 2.9.98 4.37.98 1.43 0 2.31-.98 4.35-.98 1.56 0 3.22.85 4.4 2.32-3.86 2.12-3.23 7.63.62 9.02z"/>
</svg>
              <span className="text-sm font-medium text-white">Continuar con Apple</span>
            </button>

            <button
              type="button"
              onClick={handleGoogleRedirect}
              className="flex items-center justify-center gap-3 w-full px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors shadow-sm"
              data-testid="google-login-button"
            >
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              <span className="text-sm font-medium text-gray-700">Continuar con Google</span>
            </button>
          </div>

          <div className="text-center text-sm text-gray-600">
            ¿No tienes cuenta?{' '}
            <Link
              to={
                (new URLSearchParams(window.location.search).get('redirect') || localStorage.getItem('ucan_after_login_redirect'))
                  ? `/register?redirect=${encodeURIComponent(new URLSearchParams(window.location.search).get('redirect') || localStorage.getItem('ucan_after_login_redirect'))}`
                  : '/register'
              }
              className="text-[#E6202E] hover:underline font-semibold"
              data-testid="register-link"
            >
              Regístrate aquí
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
